import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { moduleByKey } from "@/modules/registry";
import {
  ArrowUpRight, ArrowLeft, ChevronRight, ChevronDown,
  LayoutGrid, Route, Grid3x3, Sparkles,
} from "lucide-react";
import {
  PictogramMegaphone,
  PictogramWelcomeDoor,
  PictogramPosTerminal,
  PictogramStackedCrates,
  PictogramRegisterChart,
  PictogramTeamBadge,
  PictogramDatabaseGear,
} from "@/components/JourneyPictograms";
import TodayAtAGlance from "@/components/TodayAtAGlance";

/* ==================================================================
 * Business Journey — Category-First Main Menu (v2, spec-matched)
 * ------------------------------------------------------------------
 * Default view: five primary stage cards arranged in a horizontal row
 * with simple glowing arrows between them, showing the real business
 * flow from promotion to reporting. Serve & Sell is visually emphasized
 * as the main operations stage. A Business Support section appears
 * below with two support categories (Team & Access, System Settings),
 * connected by a quiet dotted line.
 *
 * Clicking any stage opens an in-page category detail view (no route
 * change) that lists permitted modules, auto-derived quick actions,
 * and the internal operational sequence for that stage. Users can
 * also switch to "All Tools" for a flat grouped module directory.
 * ================================================================== */

/* ---------------- Virtual (non-top-level) module helpers ---------------- */
// Some spec entries (e.g., "Floor & Tables") are sub-items of registry
// modules. We represent them as light virtual module objects so the
// category detail view can render them uniformly.
const virtualModule = ({ key, name, tagline, description, icon, basePath, permission }) => ({
  key, name, tagline, description, icon, basePath, permission,
  items: [],
  __virtual: true,
});

const V_FLOOR_TABLES = virtualModule({
  key: "floor_tables",
  name: "Floor & Tables",
  tagline: "Seating map & availability",
  description: "Table map, availability, and floor status.",
  icon: Grid3x3,
  basePath: "/pos/tables",
  permission: "dashboard.view",
});

/* -------------------------- Stage definitions -------------------------- */

const STAGES = [
  {
    key: "promote_book",
    num: "01",
    title: "Promote & Book",
    tag: "Marketing, guests, events, and reservations.",
    subtitle: "Create demand, attract guests, and manage bookings.",
    icon: PictogramMegaphone,
    accent: "violet",
    modules: ["marketing", "events", "guests", "reservations"],
  },
  {
    key: "welcome_seat",
    num: "02",
    title: "Welcome & Seat",
    tag: "Entrance, arrivals, host, and tables.",
    subtitle: "Manage guest arrival, entrance, seating, and table availability.",
    icon: PictogramWelcomeDoor,
    accent: "indigo",
    modules: ["security", "host", V_FLOOR_TABLES],
  },
  {
    key: "serve_sell",
    num: "03",
    title: "Serve & Sell",
    tag: "POS, orders, kitchen, and payments.",
    subtitle: "Take orders, prepare items, serve guests, and process payments.",
    icon: PictogramPosTerminal,
    accent: "sky",
    primary: true,
    modules: ["pos", "operations"],
    anchor: "pos",
  },
  {
    key: "stock_supply",
    num: "04",
    title: "Stock & Supply",
    tag: "Inventory, suppliers, purchasing, and usage.",
    subtitle: "Track product usage, manage stock, and replenish supplies.",
    icon: PictogramStackedCrates,
    accent: "cyan",
    modules: ["inventory", "purchasing", "facilities"],
  },
  {
    key: "close_review",
    num: "05",
    title: "Close & Review",
    tag: "Cash, expenses, reports, and insights.",
    subtitle: "Close daily transactions and understand business performance.",
    icon: PictogramRegisterChart,
    accent: "emerald",
    modules: ["finance", "reports"],
  },
];

const SUPPORT = [
  {
    key: "team_access",
    title: "Team & Access",
    tag: "Users, employees, payroll, roles, and permissions.",
    subtitle: "Manage your team, responsibilities, access, and payroll.",
    icon: PictogramTeamBadge,
    accent: "fuchsia",
    modules: ["team", "payroll", "audit"],
  },
  {
    key: "system_settings",
    title: "System Settings",
    tag: "Master files, facilities, branches, and configuration.",
    subtitle: "Configure the information and rules used across the system.",
    icon: PictogramDatabaseGear,
    accent: "teal",
    modules: ["masterfiles", "settings"],
  },
];

/* --------------------------- Accent styling --------------------------- */

const ACCENT = {
  sky: {
    chip: "text-sky-300",
    dot: "bg-sky-400",
    ring: "ring-sky-400/45",
    glow: "shadow-[0_30px_70px_-30px_rgba(56,189,248,0.55),0_0_40px_-10px_rgba(139,92,246,0.28)]",
    tint: "bg-[linear-gradient(160deg,rgba(56,189,248,0.14)_0%,rgba(139,92,246,0.06)_45%,rgba(15,23,42,0.55)_100%)]",
    iconWrap: "bg-sky-500/20 ring-1 ring-sky-400/40",
    iconClr: "text-sky-100",
    arrow: "text-sky-300",
  },
  violet:  { chip: "text-violet-300",  dot: "bg-violet-400",  ring: "ring-violet-400/25",  glow: "", tint: "", iconWrap: "bg-white/[0.05] ring-1 ring-white/[0.06]", iconClr: "ink", arrow: "text-sky-300" },
  indigo:  { chip: "text-indigo-300",  dot: "bg-indigo-400",  ring: "ring-indigo-400/25",  glow: "", tint: "", iconWrap: "bg-white/[0.05] ring-1 ring-white/[0.06]", iconClr: "ink", arrow: "text-sky-300" },
  cyan:    { chip: "text-cyan-300",    dot: "bg-cyan-400",    ring: "ring-cyan-400/25",    glow: "", tint: "", iconWrap: "bg-white/[0.05] ring-1 ring-white/[0.06]", iconClr: "ink", arrow: "text-sky-300" },
  emerald: { chip: "text-emerald-300", dot: "bg-emerald-400", ring: "ring-emerald-400/25", glow: "", tint: "", iconWrap: "bg-white/[0.05] ring-1 ring-white/[0.06]", iconClr: "ink", arrow: "text-sky-300" },
  fuchsia: { chip: "text-fuchsia-300", dot: "bg-fuchsia-400", ring: "ring-fuchsia-400/25", glow: "", tint: "", iconWrap: "bg-white/[0.05] ring-1 ring-white/[0.06]", iconClr: "ink", arrow: "text-sky-300" },
  teal:    { chip: "text-teal-300",    dot: "bg-teal-400",    ring: "ring-teal-400/25",    glow: "", tint: "", iconWrap: "bg-white/[0.05] ring-1 ring-white/[0.06]", iconClr: "ink", arrow: "text-sky-300" },
};

/** Hex-style tokens for detailed SVG pictograms (used as the accent color
 * on the pictogram's secondary paths). Keeps stroke/detail restrained. */
const ACCENT_HEX = {
  sky:     "rgb(125 211 252)",
  violet:  "rgb(196 181 253)",
  indigo:  "rgb(165 180 252)",
  cyan:    "rgb(103 232 249)",
  emerald: "rgb(110 231 183)",
  fuchsia: "rgb(240 171 252)",
  teal:    "rgb(94 234 212)",
};

const NODE_DESC = {
  pos: "Transactions, orders, tables, and payments.",
  reservations: "Bookings, waitlists, and arrivals.",
  guests: "Profiles, VIP tiers, and loyalty.",
  events: "Events, promoters, and attendance.",
  marketing: "Campaigns, offers, and audiences.",
  host: "Seating, arrivals, and guest flow.",
  security: "Door access and occupancy.",
  operations: "Tasks, handover, and oversight.",
  inventory: "Stock levels and usage.",
  purchasing: "Orders, suppliers, and deliveries.",
  masterfiles: "Products, menus, and taxes.",
  facilities: "Assets, maintenance, and incidents.",
  finance: "Cash, expenses, and reconciliation.",
  reports: "Performance and analytics.",
  payroll: "People, attendance, and payroll.",
  team: "Users, roles, and permissions.",
  audit: "Activity trail and privacy.",
  settings: "System rules and preferences.",
  floor_tables: "Table map, availability, and floor status.",
};

/* -------------- Resolve module refs (string key OR virtual obj) -------------- */
const resolveModuleRef = (ref) => (typeof ref === "string" ? moduleByKey(ref) : ref);

/* ==================================================================== Page */

export default function HubPage() {
  const { user, hasPerm } = useAuth();
  const [view, setView] = useState("journey"); // "journey" | "all"
  const [activeKey, setActiveKey] = useState(null);

  const canSee = (m) => !!m && (!m.permission || hasPerm(m.permission));
  const canSeeItem = (item) => !item.perm || hasPerm(item.perm);

  const firstName = (user?.name || user?.email || "Operator").split(" ")[0];

  const { visibleStages, visibleSupport, allCategories } = useMemo(() => {
    const buildVisible = (list) =>
      list
        .map((c) => ({
          ...c,
          visibleModules: c.modules.map(resolveModuleRef).filter(Boolean).filter(canSee),
        }))
        .filter((c) => c.visibleModules.length > 0);

    const s = buildVisible(STAGES);
    const t = buildVisible(SUPPORT);
    return { visibleStages: s, visibleSupport: t, allCategories: [...s, ...t] };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPerm]);

  const currentCategory =
    (activeKey && allCategories.find((c) => c.key === activeKey)) || null;

  return (
    <div className="h-full w-full surface flex flex-col overflow-hidden" data-testid="hub-page">
      <TopBar
        currentCategory={currentCategory}
        firstName={firstName}
        view={view}
        onView={(v) => { setView(v); setActiveKey(null); }}
        onBack={() => setActiveKey(null)}
      />

      <section className="flex-1 min-h-0 overflow-y-auto">
        {view === "all" ? (
          <AllTools categories={allCategories} />
        ) : currentCategory ? (
          <CategoryDetail
            category={currentCategory}
            canSeeItem={canSeeItem}
            onBack={() => setActiveKey(null)}
          />
        ) : (
          <JourneyView
            stages={visibleStages}
            support={visibleSupport}
            onOpen={setActiveKey}
          />
        )}
      </section>
    </div>
  );
}

/* ================================================================ Top Bar */

function TopBar({ currentCategory, firstName, view, onView, onBack }) {
  return (
    <section className="relative overflow-hidden border-b hairline-soft shrink-0">
      <div className="relative w-[min(1680px,calc(100%-64px))] mx-auto py-4 flex items-center justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          {currentCategory ? (
            <Breadcrumb category={currentCategory} onBack={onBack} />
          ) : (
            <span className="tiny-label ink-subtle">Hi {firstName}</span>
          )}
        </div>
        <ViewSwitcher view={view} onChange={onView} />
      </div>
    </section>
  );
}

function Breadcrumb({ category, onBack }) {
  return (
    <div className="flex items-center gap-2 min-w-0" data-testid="hub-breadcrumb">
      <button
        onClick={onBack}
        data-testid="hub-breadcrumb-back"
        className="h-8 px-2.5 rounded-full text-[11.5px] font-semibold flex items-center gap-1.5 ink-muted hover:ink hover:bg-foreground/[0.05] transition-colors"
      >
        <ArrowLeft size={12} /> Back
      </button>
      <div className="flex items-center gap-1.5 min-w-0 text-[13px]">
        <button onClick={onBack} className="ink-muted hover:ink transition-colors truncate">
          Business Journey
        </button>
        <ChevronRight size={13} className="ink-subtle shrink-0" />
        <span className="ink font-semibold truncate">{category.title}</span>
      </div>
    </div>
  );
}

function ViewSwitcher({ view, onChange }) {
  return (
    <div className="inline-flex rounded-full border hairline surface-card p-1" role="tablist">
      <ViewBtn active={view === "journey"} onClick={() => onChange("journey")} icon={Route}       label="Business Journey" testId="hub-view-journey" />
      <ViewBtn active={view === "all"}     onClick={() => onChange("all")}     icon={LayoutGrid}  label="All Tools"        testId="hub-view-all" />
    </div>
  );
}

function ViewBtn({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      role="tab"
      aria-selected={active}
      className={`h-8 px-3.5 rounded-full text-[11px] font-semibold flex items-center gap-1.5 transition-colors ${
        active ? "bg-foreground text-background" : "ink-muted hover:ink"
      }`}
    >
      <Icon size={11} />
      <span>{label}</span>
    </button>
  );
}

/* ============================================================ Journey view */

function JourneyView({ stages, support, onOpen }) {
  return (
    <div className="relative min-h-full pb-14">
      {/* Background lighting */}
      <div aria-hidden className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[120px] left-1/2 -translate-x-1/2 h-[520px] w-[880px] rounded-full bg-sky-500/[0.05] blur-3xl" />
        <div className="absolute top-[180px] left-1/2 -translate-x-1/2 h-[300px] w-[420px] rounded-full bg-violet-500/[0.05] blur-3xl" />
      </div>

      <div className="relative w-[min(1680px,calc(100%-64px))] mx-auto pt-6 md:pt-8">
        {/* Today at a Glance — compact live snapshot */}
        <TodayAtAGlance />

        {/* Page title */}
        <header className="text-center max-w-[720px] mx-auto mt-10 md:mt-12">
          <h1
            data-testid="hub-title"
            className="display-heading text-[36px] md:text-[42px] ink leading-none tracking-tight"
          >
            Business Journey
          </h1>
          <p className="mt-3 text-[15px] md:text-[16px] ink-muted">
            Follow the real business flow from promotion to reporting.
          </p>
        </header>

        {/* Journey row — single DOM, responsive layout via CSS */}
        <div
          data-testid="journey-row"
          className="mt-8 md:mt-10 flex flex-col md:flex-row md:flex-wrap lg:flex-nowrap items-stretch justify-center gap-3"
        >
          {stages.map((s, i) => (
            <React.Fragment key={s.key}>
              <StageCard stage={s} onOpen={onOpen} />
              {i < stages.length - 1 && <StageArrow />}
            </React.Fragment>
          ))}
        </div>

        {/* Business Support */}
        {support.length > 0 && (
          <SupportSection support={support} onOpen={onOpen} />
        )}
      </div>
    </div>
  );
}

function StageArrow() {
  // Responsive: right chevron on lg+, down chevron on mobile,
  // hidden on md (cards wrap naturally to 3+2).
  return (
    <div
      aria-hidden
      className="shrink-0 self-center flex items-center justify-center w-full h-7 md:hidden lg:flex lg:w-8 lg:h-auto"
    >
      <span
        className="inline-flex items-center justify-center rounded-full
          h-7 w-7 bg-sky-400/10 ring-1 ring-sky-400/25
          shadow-[0_0_18px_-2px_rgba(56,189,248,0.45)]"
      >
        <ChevronRight size={14} className="hidden lg:block text-sky-300" />
        <ChevronDown  size={14} className="lg:hidden text-sky-300" />
      </span>
    </div>
  );
}

function StageCard({ stage, onOpen }) {
  const a = ACCENT[stage.accent] || ACCENT.violet;
  const Icon = stage.icon;
  const primary = !!stage.primary;
  const count = stage.visibleModules.length;

  // Widths: mobile full, md ~ 1/3 of row (produces 3+2 wrap), lg exact px.
  const baseWidth = primary
    ? "w-full md:w-[calc((100%-1.5rem)/3)] lg:w-[268px]"
    : "w-full md:w-[calc((100%-1.5rem)/3)] lg:w-[224px]";
  const baseHeight = primary
    ? "min-h-[300px] lg:min-h-[318px]"
    : "min-h-[264px] lg:min-h-[284px]";

  return (
    <button
      onClick={() => onOpen(stage.key)}
      data-testid={`stage-card-${stage.key}`}
      aria-label={`Open ${stage.title} — ${count} ${count === 1 ? "module" : "modules"}`}
      className={`group relative text-left rounded-[22px] overflow-hidden p-6
        ${baseWidth} ${baseHeight}
        border border-white/[0.06]
        bg-[linear-gradient(180deg,rgba(255,255,255,0.02),rgba(15,23,42,0.55))]
        shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]
        transition-all duration-[180ms] ease-out
        hover:-translate-y-[2px] hover:border-white/[0.16]
        focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/50
        motion-reduce:transition-none motion-reduce:hover:translate-y-0
        ${primary ? `ring-1 ${a.ring} ${a.glow} ${a.tint}` : ""}`}
    >
      {primary && (
        <>
          <span aria-hidden className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-sky-400/20 blur-3xl pointer-events-none" />
          <span aria-hidden className="absolute -left-10 -bottom-10 h-40 w-40 rounded-full bg-violet-500/15 blur-3xl pointer-events-none" />
        </>
      )}

      {/* Header row */}
      <div className="relative flex items-start justify-between gap-3">
        <div
          className={`shrink-0 flex items-start justify-start
            ${primary ? "w-[76px] h-[76px]" : "w-[64px] h-[64px]"}`}
          aria-hidden
        >
          <Icon
            size={primary ? 68 : 56}
            accent={ACCENT_HEX[stage.accent] || ACCENT_HEX.sky}
            className="text-white/95"
          />
        </div>
        <span className={`mono text-[11px] font-semibold ${a.chip} tracking-[0.18em]`}>
          {stage.num}
        </span>
      </div>

      {/* Primary label */}
      {primary && (
        <div className="relative mt-4">
          <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] font-semibold bg-sky-500/15 text-sky-200 ring-1 ring-sky-400/30">
            <Sparkles size={10} /> Main Operations
          </span>
        </div>
      )}

      {/* Title + tag */}
      <div className={`relative ${primary ? "mt-3" : "mt-5"}`}>
        <div className="flex items-center gap-2">
          <h2 className={`font-semibold ink tracking-tight leading-none ${primary ? "text-[21px]" : "text-[19px]"}`}>
            {stage.title}
          </h2>
          <span className={`h-1.5 w-1.5 rounded-full ${a.dot} opacity-80`} />
        </div>
        <p className="mt-2 text-[13.5px] ink-muted leading-snug">{stage.tag}</p>
      </div>

      {/* Footer */}
      <div className="absolute left-6 right-6 bottom-5 flex items-center justify-between">
        <span className="text-[11px] ink-subtle">
          {count} {count === 1 ? "module" : "modules"}
        </span>
        <span className="flex items-center gap-1 text-[11.5px] font-semibold ink transition-transform group-hover:translate-x-0.5">
          Open <ArrowUpRight size={12} />
        </span>
      </div>
    </button>
  );
}

/* ================================================= Business Support region */

function SupportSection({ support, onOpen }) {
  return (
    <section className="relative mt-14 md:mt-16" data-testid="support-section">
      {/* Dotted connector: short vertical drop + horizontal spread */}
      <div aria-hidden className="relative h-10 flex flex-col items-center justify-start pointer-events-none">
        <div className="h-6 border-l border-dotted border-white/15" />
        <div className="h-px w-[min(720px,80%)] border-t border-dotted border-white/12" />
      </div>

      <header className="mt-4 text-center">
        <h3 className="text-[14px] uppercase tracking-[0.22em] font-semibold ink-muted">
          Business Support
        </h3>
        <p className="mt-1 text-[12.5px] ink-subtle">Powering every stage</p>
      </header>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-[820px] mx-auto">
        {support.map((s) => (
          <SupportCard key={s.key} category={s} onOpen={onOpen} />
        ))}
      </div>
    </section>
  );
}

function SupportCard({ category, onOpen }) {
  const a = ACCENT[category.accent] || ACCENT.violet;
  const Icon = category.icon;
  const count = category.visibleModules.length;
  return (
    <button
      onClick={() => onOpen(category.key)}
      data-testid={`support-card-${category.key}`}
      aria-label={`Open ${category.title}`}
      className="group relative text-left rounded-2xl overflow-hidden p-5
        border border-white/[0.06]
        bg-[linear-gradient(180deg,rgba(255,255,255,0.02),rgba(15,23,42,0.45))]
        shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]
        transition-all duration-[180ms] ease-out
        hover:-translate-y-[2px] hover:border-white/[0.16]
        focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/50
        motion-reduce:transition-none motion-reduce:hover:translate-y-0"
    >
      <div className="flex items-center gap-3">
        <div className="w-[52px] h-[52px] shrink-0 flex items-center justify-center" aria-hidden>
          <Icon
            size={44}
            accent={ACCENT_HEX[category.accent] || ACCENT_HEX.sky}
            className="text-white/95"
          />
        </div>
        <div className="min-w-0 flex-1 leading-tight">
          <div className="flex items-center gap-2">
            <div className="text-[15px] font-semibold ink truncate">{category.title}</div>
            <span className={`h-1.5 w-1.5 rounded-full ${a.dot} opacity-80`} />
          </div>
          <div className="mt-1 text-[12px] ink-muted truncate">{category.tag}</div>
        </div>
        <ArrowUpRight size={14} className="ink-subtle opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
      <div className="mt-4 pt-3 border-t hairline-soft text-[11px] ink-subtle">
        {count} {count === 1 ? "module" : "modules"}
      </div>
    </button>
  );
}

/* ========================================================= Category Detail */

function CategoryDetail({ category, canSeeItem, onBack }) {
  const a = ACCENT[category.accent] || ACCENT.violet;

  // Auto-derived quick actions: pick up to 6 permitted registry items,
  // prioritising the anchor module first, then following the order the
  // spec defines (already reflected in category.modules).
  const quickActions = useMemo(() => {
    const ordered = [...category.visibleModules].sort((m1, m2) => {
      if (category.anchor && m1.key === category.anchor) return -1;
      if (category.anchor && m2.key === category.anchor) return 1;
      return 0;
    });
    const actions = [];
    for (const mod of ordered) {
      // Virtual modules have no sub-items; use their basePath directly
      if (mod.__virtual) {
        actions.push({
          module: mod,
          item: { label: mod.name, path: mod.basePath, icon: mod.icon },
        });
        if (actions.length >= 6) return actions;
        continue;
      }
      const items = (mod.items || []).filter(canSeeItem);
      for (const item of items) {
        actions.push({ module: mod, item });
        if (actions.length >= 6) return actions;
      }
    }
    return actions;
  }, [category, canSeeItem]);

  return (
    <div className="relative min-h-full pb-10">
      <div className="relative w-[min(1440px,calc(100%-48px))] mx-auto py-6 space-y-6">
        {/* Hero */}
        <div
          className={`relative rounded-2xl overflow-hidden p-6 border border-white/[0.06]
            bg-[linear-gradient(180deg,rgba(255,255,255,0.02),rgba(15,23,42,0.45))]
            ${category.primary ? `ring-1 ${a.ring} ${a.glow} ${a.tint}` : ""}`}
          data-testid={`category-detail-${category.key}`}
        >
          {category.primary && (
            <span aria-hidden className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-sky-400/25 blur-3xl pointer-events-none" />
          )}
          <div className="relative flex items-start gap-4">
            <div className="w-[64px] h-[64px] shrink-0 flex items-center justify-center" aria-hidden>
              <category.icon
                size={56}
                accent={ACCENT_HEX[category.accent] || ACCENT_HEX.sky}
                className="text-white/95"
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                {category.num && (
                  <span className={`mono text-[10px] font-semibold ${a.chip} tracking-[0.18em]`}>{category.num}</span>
                )}
                <span className={`h-1 w-1 rounded-full ${a.dot} opacity-70`} />
                <span className="text-[10.5px] uppercase tracking-[0.18em] ink-subtle">Stage</span>
              </div>
              <h2 className="mt-1.5 text-[22px] md:text-[26px] font-semibold ink tracking-tight leading-none">
                {category.title}
              </h2>
              <p className="mt-2 text-[13px] ink-muted">{category.subtitle || category.tag}</p>
            </div>
          </div>
        </div>

        {/* Quick actions */}
        {quickActions.length > 0 && (
          <div data-testid="category-quick-actions">
            <div className="flex items-center gap-2 mb-2.5">
              <h3 className="text-[13.5px] font-semibold ink tracking-tight">Quick actions</h3>
              <span className="text-[11px] ink-subtle">Jump straight to the most-used screens</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
              {quickActions.map(({ module, item }, idx) => {
                const isAnchor = category.anchor && module.key === category.anchor && idx === 0;
                const ItemIcon = item.icon || module.icon;
                return (
                  <Link
                    key={`${module.key}-${item.path}`}
                    to={item.path}
                    data-testid={`category-quick-${module.key}-${item.path.replace(/[/]/g, "-")}`}
                    className={`group h-[62px] flex items-center gap-2.5 px-3 rounded-xl transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/40 ${
                      isAnchor
                        ? "ring-1 ring-sky-400/40 bg-sky-500/[0.06] hover:bg-sky-500/[0.10]"
                        : "border border-white/[0.07] bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/[0.16]"
                    }`}
                  >
                    <span className={`h-9 w-9 shrink-0 grid place-items-center rounded-lg ${isAnchor ? "bg-sky-500/20 ring-1 ring-sky-400/30" : "bg-white/[0.05]"}`}>
                      <ItemIcon size={14} className={isAnchor ? "text-sky-200" : "ink"} />
                    </span>
                    <span className="flex-1 min-w-0 leading-tight">
                      <span className="block text-[12.5px] font-semibold ink truncate">{item.label}</span>
                      <span className="block text-[10.5px] ink-subtle truncate mt-0.5">{module.name}</span>
                    </span>
                    <ArrowUpRight size={11} className="ink-subtle opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Modules */}
        <div data-testid="category-modules">
          <div className="flex items-center gap-2 mb-2.5">
            <h3 className="text-[13.5px] font-semibold ink tracking-tight">Modules</h3>
            <span className="text-[11px] ink-subtle">
              {category.visibleModules.length} available
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {category.visibleModules.map((mod) => (
              <ModuleCard key={mod.key} mod={mod} anchor={category.anchor === mod.key} />
            ))}
          </div>
        </div>

        <div className="pt-2">
          <button
            onClick={onBack}
            data-testid="hub-back-bottom"
            className="h-9 px-4 rounded-full text-[12px] font-semibold flex items-center gap-1.5 ink-muted hover:ink border hairline hover:bg-foreground/[0.05] transition-colors"
          >
            <ArrowLeft size={12} /> Back to Business Journey
          </button>
        </div>
      </div>
    </div>
  );
}

function ModuleCard({ mod, anchor = false }) {
  const Icon = mod.icon;
  const desc = NODE_DESC[mod.key] || mod.tagline;
  return (
    <Link
      to={mod.basePath}
      data-testid={`category-module-${mod.key}`}
      className={`group relative flex items-center gap-3 rounded-xl overflow-hidden px-4 py-3.5
        transition-all duration-150 hover:-translate-y-[1px]
        focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/50
        motion-reduce:transition-none motion-reduce:hover:translate-y-0
        ${anchor
          ? "ring-1 ring-sky-400/45 bg-[linear-gradient(140deg,rgba(56,189,248,0.16),rgba(15,23,42,0.9))] shadow-[0_18px_50px_-25px_rgba(56,189,248,0.5)]"
          : "border border-white/[0.07] bg-[linear-gradient(180deg,rgba(255,255,255,0.03),rgba(15,23,42,0.55))] hover:border-white/[0.18] hover:bg-[linear-gradient(180deg,rgba(255,255,255,0.06),rgba(30,41,59,0.65))] shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]"
        }`}
    >
      <span className={`h-10 w-10 shrink-0 grid place-items-center rounded-lg ${anchor ? "bg-sky-500/25 ring-1 ring-sky-400/40" : "bg-white/[0.05] ring-1 ring-white/[0.06]"}`}>
        <Icon size={16} className={anchor ? "text-sky-100" : "ink"} />
      </span>
      <div className="min-w-0 flex-1 leading-tight">
        <div className={`font-semibold truncate ${anchor ? "text-[14.5px] text-white" : "text-[13.5px] ink"}`}>{mod.name}</div>
        <div className="text-[11px] ink-muted line-clamp-2 mt-0.5 leading-[1.35]">{desc}</div>
      </div>
      <ArrowUpRight size={12} className="ink-subtle opacity-0 group-hover:opacity-100 transition-opacity" />
    </Link>
  );
}

/* ================================================================ All Tools */

function AllTools({ categories }) {
  return (
    <div className="w-[min(1540px,calc(100%-48px))] mx-auto py-6 space-y-6" data-testid="all-tools">
      {categories.map((cat, gi) => (
        <div
          key={cat.key}
          data-testid={`all-tools-group-${cat.key}`}
          className="animate-fade-up"
          style={{ animationDelay: `${gi * 40}ms` }}
        >
          <div className="flex items-center gap-2 mb-2.5">
            {cat.num && (
              <span className={`mono text-[10px] font-semibold ${ACCENT[cat.accent]?.chip || ""} tracking-[0.18em]`}>{cat.num}</span>
            )}
            <h2 className="text-[14px] font-semibold ink tracking-tight">{cat.title}</h2>
            <span className={`h-1.5 w-1.5 rounded-full ${ACCENT[cat.accent]?.dot || ""} opacity-70`} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2.5">
            {cat.visibleModules.map((m) => {
              const anchor = cat.anchor === m.key;
              const Icon = m.icon;
              return (
                <Link
                  key={m.key}
                  to={m.basePath}
                  data-testid={`module-card-${m.key}`}
                  className={`group rounded-xl p-3.5 flex flex-col gap-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/50
                    ${anchor
                      ? "col-span-2 ring-1 ring-sky-400/55 bg-[linear-gradient(140deg,rgba(56,189,248,0.16),rgba(15,23,42,0.9))] shadow-[0_20px_50px_-25px_rgba(56,189,248,0.5)]"
                      : "border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/[0.16]"}`}
                >
                  <div className="flex items-start justify-between">
                    <span className={`h-10 w-10 grid place-items-center rounded-lg ${anchor ? "bg-sky-500/25 ring-1 ring-sky-400/40" : "bg-white/[0.05] ring-1 ring-white/[0.05]"}`}>
                      <Icon size={16} className={anchor ? "text-sky-100" : "ink"} />
                    </span>
                    <ArrowUpRight size={11} className="ink-subtle opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div>
                    <div className={`font-semibold tracking-tight ${anchor ? "text-[15px] text-white uppercase" : "text-[13px] ink"}`}>{m.name}</div>
                    <p className="mt-1 text-[11px] ink-muted leading-snug line-clamp-2">{NODE_DESC[m.key] || m.tagline}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
